/**
 * SpeedmasterCore.Lightbox.js — Fullscreen Image Lightbox
 *
 * Desktop only. Click gallery image → fullscreen dark overlay.
 * Navigate images + video. No zoom/pan — fullscreen IS the zoom.
 * Mobile returns immediately (native pinch-spread suffices).
 *
 * Singleton — one lightbox instance, reused across SPA navigation.
 * DOM created lazily on first open, appended to document.body.
 *
 * Dependencies: underscore only. ES5. No jQuery plugins.
 */
define("SpeedmasterCore.Lightbox", [
	"underscore",
	"Utilities.ResizeImage"
], function(_, resizeImage) {
	"use strict";

	// ── Constants ────────────────────────────────────────────────
	var DESKTOP_MIN = 992;

	// ── State ────────────────────────────────────────────────────
	var state = {
		isOpen: false,
		images: [],
		videoData: null,
		currentIndex: 0,
		isShowingVideo: false,
		savedScrollY: 0,
		returnFocusEl: null
	};

	// ── DOM references (populated on first createDOM) ────────────
	var dom = {
		root: null,
		backdrop: null,
		closeBtn: null,
		counter: null,
		stage: null,
		image: null,
		videoWrap: null,
		prevBtn: null,
		nextBtn: null,
		thumbs: null
	};

	var keydownHandler = null;
	var domCreated = false;

	// ── Helpers ──────────────────────────────────────────────────

	/**
	 * Total number of slides (images + optional video).
	 * When video exists: index 0 = video, index 1+ = images.
	 */
	function totalSlides() {
		return state.images.length + (state.videoData ? 1 : 0);
	}

	/**
	 * Check if a given combined index is the video slot.
	 */
	function isVideoIndex(index) {
		return !!state.videoData && index === 0;
	}

	/**
	 * Convert a combined index to an image array index.
	 * When video exists, combined index 1 → images[0], etc.
	 */
	function toImageIndex(combinedIndex) {
		return state.videoData ? combinedIndex - 1 : combinedIndex;
	}

	/**
	 * Convert an image array index to a combined index.
	 * When video exists, images[0] → combined index 1, etc.
	 */
	function toCombinedIndex(imageIndex) {
		return state.videoData ? imageIndex + 1 : imageIndex;
	}

	/**
	 * Create an SVG element string following V4 icon rules.
	 * viewBox 0 0 24 24, 16x16, stroke currentColor, 2.5, round.
	 */
	function iconSvg(content) {
		return '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
			'stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
			content + '</svg>';
	}

	// ── DOM Creation ────────────────────────────────────────────

	function createDOM() {
		if (domCreated) { return; }

		var root = document.createElement("div");
		root.className = "sm-lightbox";
		root.setAttribute("role", "dialog");
		root.setAttribute("aria-modal", "true");
		root.setAttribute("aria-label", "Image gallery");

		// Backdrop
		var backdrop = document.createElement("div");
		backdrop.className = "sm-lightbox-backdrop";
		root.appendChild(backdrop);

		// Close button
		var closeBtn = document.createElement("button");
		closeBtn.className = "sm-lightbox-close";
		closeBtn.setAttribute("aria-label", "Close");
		closeBtn.innerHTML = iconSvg('<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>');
		root.appendChild(closeBtn);

		// Counter
		var counter = document.createElement("span");
		counter.className = "sm-lightbox-counter";
		root.appendChild(counter);

		// Stage
		var stage = document.createElement("div");
		stage.className = "sm-lightbox-stage";
		root.appendChild(stage);

		// Main image
		var image = document.createElement("img");
		image.className = "sm-lightbox-image";
		image.setAttribute("alt", "");
		image.setAttribute("draggable", "false");
		stage.appendChild(image);

		// Video wrap
		var videoWrap = document.createElement("div");
		videoWrap.className = "sm-lightbox-video-wrap";
		videoWrap.style.display = "none";
		stage.appendChild(videoWrap);

		// Prev arrow
		var prevBtn = document.createElement("button");
		prevBtn.className = "sm-lightbox-arrow sm-lightbox-prev";
		prevBtn.setAttribute("aria-label", "Previous image");
		prevBtn.innerHTML = iconSvg('<polyline points="15 18 9 12 15 6"/>');
		root.appendChild(prevBtn);

		// Next arrow
		var nextBtn = document.createElement("button");
		nextBtn.className = "sm-lightbox-arrow sm-lightbox-next";
		nextBtn.setAttribute("aria-label", "Next image");
		nextBtn.innerHTML = iconSvg('<polyline points="9 6 15 12 9 18"/>');
		root.appendChild(nextBtn);

		// Thumbs container
		var thumbs = document.createElement("div");
		thumbs.className = "sm-lightbox-thumbs";
		root.appendChild(thumbs);

		// Append to body
		document.body.appendChild(root);

		// Store references
		dom.root = root;
		dom.backdrop = backdrop;
		dom.closeBtn = closeBtn;
		dom.counter = counter;
		dom.stage = stage;
		dom.image = image;
		dom.videoWrap = videoWrap;
		dom.prevBtn = prevBtn;
		dom.nextBtn = nextBtn;
		dom.thumbs = thumbs;

		// ── Event listeners (permanent — DOM is reused) ─────────

		// Close: backdrop click
		backdrop.addEventListener("click", function() {
			close();
		});

		// Close: X button
		closeBtn.addEventListener("click", function() {
			close();
		});

		// Navigate: prev
		prevBtn.addEventListener("click", function(e) {
			e.stopPropagation();
			goPrev();
		});

		// Navigate: next
		nextBtn.addEventListener("click", function(e) {
			e.stopPropagation();
			goNext();
		});

		// Thumbnails: delegated click
		thumbs.addEventListener("click", function(e) {
			var btn = e.target;
			// Walk up to the button if click was on child (img, icon)
			while (btn && btn !== thumbs) {
				if (btn.hasAttribute("data-lb-index")) {
					var idx = parseInt(btn.getAttribute("data-lb-index"), 10);
					if (!isNaN(idx)) { showSlide(idx); }
					return;
				}
				btn = btn.parentElement;
			}
		});

		// Prevent image drag
		image.addEventListener("dragstart", function(e) {
			e.preventDefault();
		});

		domCreated = true;
	}

	// ── Build Thumbnails ────────────────────────────────────────

	function buildThumbs() {
		var container = dom.thumbs;
		container.innerHTML = "";

		var total = totalSlides();

		// Hide thumbs entirely if only 1 slide
		if (total <= 1) {
			container.style.display = "none";
			return;
		}
		container.style.display = "";

		var combinedIndex = 0;

		// Video thumb first (if exists)
		if (state.videoData) {
			var vBtn = document.createElement("button");
			vBtn.className = "sm-lightbox-thumb sm-lightbox-thumb-video";
			vBtn.setAttribute("data-lb-index", "0");
			vBtn.setAttribute("aria-label", "Play video");

			var vImg = document.createElement("img");
			vImg.src = state.videoData.thumbUrl;
			vImg.alt = "Product video";
			vBtn.appendChild(vImg);

			// Play icon overlay
			var playIcon = document.createElement("span");
			playIcon.className = "sm-lightbox-thumb-play-icon";
			playIcon.setAttribute("aria-hidden", "true");
			playIcon.innerHTML = '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" stroke="none">' +
				'<polygon points="6 3 20 12 6 21"/></svg>';
			vBtn.appendChild(playIcon);

			container.appendChild(vBtn);
			combinedIndex = 1;
		}

		// Image thumbs
		for (var i = 0; i < state.images.length; i++) {
			var btn = document.createElement("button");
			btn.className = "sm-lightbox-thumb";
			btn.setAttribute("data-lb-index", String(combinedIndex));
			btn.setAttribute("aria-label", "View image " + (i + 1));

			var img = document.createElement("img");
			img.src = resizeImage(state.images[i].url, "tinythumb");
			img.alt = state.images[i].altimagetext || "";
			btn.appendChild(img);

			container.appendChild(btn);
			combinedIndex++;
		}
	}

	// ── Show Slide ──────────────────────────────────────────────

	function showSlide(index) {
		var total = totalSlides();
		if (index < 0 || index >= total) { return; }

		state.currentIndex = index;

		if (isVideoIndex(index)) {
			// Show video
			state.isShowingVideo = true;
			dom.image.style.display = "none";

			dom.videoWrap.innerHTML =
				'<div class="sm-lightbox-video-inner">' +
					'<iframe src="' + state.videoData.embedUrl + '&autoplay=1" ' +
						'allowfullscreen ' +
						'allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" ' +
						'title="Product video">' +
					'</iframe>' +
				'</div>';
			dom.videoWrap.style.display = "";

		} else {
			// Show image
			state.isShowingVideo = false;
			// Stop video if it was playing
			dom.videoWrap.innerHTML = "";
			dom.videoWrap.style.display = "none";

			var imgIdx = toImageIndex(index);
			var imgData = state.images[imgIdx];

			dom.image.src = imgData.url;
			dom.image.alt = imgData.altimagetext || "";
			dom.image.style.display = "";

			// Re-trigger entrance animation
			dom.image.style.animation = "none";
			// Force reflow
			void dom.image.offsetWidth;
			dom.image.style.animation = "";
		}

		// Update counter
		dom.counter.textContent = (index + 1) + " of " + total;
		dom.counter.style.display = total > 1 ? "" : "none";

		// Update arrows — no wrap-around, hidden at boundaries
		dom.prevBtn.style.display = index <= 0 ? "none" : "";
		dom.nextBtn.style.display = index >= total - 1 ? "none" : "";

		// Update active thumb
		var thumbBtns = dom.thumbs.querySelectorAll(".sm-lightbox-thumb");
		for (var t = 0; t < thumbBtns.length; t++) {
			var tbIdx = parseInt(thumbBtns[t].getAttribute("data-lb-index"), 10);
			if (tbIdx === index) {
				thumbBtns[t].classList.add("active");
			} else {
				thumbBtns[t].classList.remove("active");
			}
		}
	}

	// ── Navigate ────────────────────────────────────────────────

	function goNext() {
		var total = totalSlides();
		if (state.currentIndex < total - 1) {
			showSlide(state.currentIndex + 1);
		}
	}

	function goPrev() {
		if (state.currentIndex > 0) {
			showSlide(state.currentIndex - 1);
		}
	}

	// ── Keyboard ────────────────────────────────────────────────

	function handleKeydown(e) {
		if (!state.isOpen) { return; }

		switch (e.keyCode) {
			case 27: // Escape
				close();
				e.preventDefault();
				break;

			case 37: // Left arrow
				goPrev();
				e.preventDefault();
				break;

			case 39: // Right arrow
				goNext();
				e.preventDefault();
				break;

			case 9: // Tab — focus trap
				trapFocus(e);
				break;
		}
	}

	/**
	 * Trap focus within the lightbox when Tab is pressed.
	 */
	function trapFocus(e) {
		var focusable = dom.root.querySelectorAll(
			'button:not([style*="display: none"]):not([style*="display:none"])'
		);
		if (!focusable.length) { return; }

		var first = focusable[0];
		var last = focusable[focusable.length - 1];

		if (e.shiftKey) {
			// Shift+Tab: if on first, go to last
			if (document.activeElement === first) {
				e.preventDefault();
				last.focus();
			}
		} else {
			// Tab: if on last, go to first
			if (document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}
	}

	// ── Open / Close ────────────────────────────────────────────

	/**
	 * Open the lightbox.
	 * @param {Array} images — array of {url, altimagetext}
	 * @param {Object|null} videoData — {embedUrl, thumbUrl, videoId} or null
	 * @param {number} startIndex — image array index to start on
	 * @param {boolean} isVideoStart — true to start on video
	 * @param {HTMLElement} returnFocusEl — element to return focus to on close
	 */
	function open(images, videoData, startIndex, isVideoStart, returnFocusEl) {
		// Desktop only
		if (window.innerWidth < DESKTOP_MIN) { return; }

		// Guard: need something to show
		if (!images || !images.length) { return; }

		createDOM();

		// Store state
		state.images = images;
		state.videoData = videoData || null;
		state.returnFocusEl = returnFocusEl || null;
		state.isOpen = true;

		// Build thumbnails
		buildThumbs();

		// Determine starting slide in combined sequence
		var startCombined;
		if (isVideoStart && state.videoData) {
			startCombined = 0; // video slot
		} else {
			startCombined = toCombinedIndex(startIndex || 0);
		}

		// Show starting slide
		showSlide(startCombined);

		// Open the overlay
		dom.root.classList.add("sm-lightbox-open");

		// Lock body scroll
		state.savedScrollY = window.pageYOffset || document.documentElement.scrollTop;
		document.body.classList.add("sm-lightbox-body-locked");

		// Focus close button
		dom.closeBtn.focus();

		// Attach keyboard
		keydownHandler = handleKeydown;
		document.addEventListener("keydown", keydownHandler);
	}

	/**
	 * Close the lightbox.
	 */
	function close() {
		if (!state.isOpen) { return; }
		state.isOpen = false;

		// Close overlay
		dom.root.classList.remove("sm-lightbox-open");

		// Stop video
		dom.videoWrap.innerHTML = "";
		dom.videoWrap.style.display = "none";

		// Unlock body scroll
		document.body.classList.remove("sm-lightbox-body-locked");

		// Detach keyboard
		if (keydownHandler) {
			document.removeEventListener("keydown", keydownHandler);
			keydownHandler = null;
		}

		// Return focus
		if (state.returnFocusEl && typeof state.returnFocusEl.focus === "function") {
			state.returnFocusEl.focus();
		}
	}

	/**
	 * Check if the lightbox is currently open.
	 */
	function isOpen() {
		return state.isOpen;
	}

	// ── Public API ──────────────────────────────────────────────

	return {
		open: open,
		close: close,
		isOpen: isOpen
	};
});
